import { Link, useLocation, useNavigate } from "react-router-dom";
import { ShoppingCart, User, Menu, X, ChevronDown, Globe, LogOut, Package } from "lucide-react";
import { useState, useEffect } from "react";
import { useCart } from "@/lib/cart";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";
import api, { resolveImage } from "@/lib/api";

export default function Header({ transparent = false, onLight = false }) {
  const { count } = useCart();
  const { lang, setLang, t } = useI18n();
  const { user, logout, isCustomer, isAdmin, isManager, isStaff } = useAuth();
  const isLoggedIn = isCustomer || isStaff;
  // Single profile button — clicking it routes based on role:
  //  - Admin   → /admin (admin dashboard)
  //  - Manager → /admin (shared dashboard, gated by permissions)
  //  - Client  → /compte (customer profile page)
  const profileTarget = isAdmin ? "/admin" : isManager ? "/admin" : "/compte";
  const profileLabel = isStaff ? "Dashboard" : "My account";
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [content, setContent] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    api.get("/content").then((r) => setContent(r.data.content)).catch(() => {});
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setAccountOpen(false);
    setLangOpen(false);
  }, [location.pathname]);

  // dark when used over hero green; light when over white pages
  const dark = transparent && !scrolled;

  const NAV_LINKS = [
    { to: "/", label: lang === "fr" ? "Accueil" : "Home" },
    { to: "/a-propos", label: lang === "fr" ? "À propos" : "About Us" },
    { to: "/univers/street_food", label: lang === "fr" ? "Menu" : "Menu & Flavors" },
    { to: "/evenements", label: "Events" },
    { to: "/magazine", label: lang === "fr" ? "Avis" : "Reviews" },
  ];

  const headerCls = transparent
    ? (scrolled ? "bg-white/95 backdrop-blur-xl border-b border-black/5 shadow-sm" : "bg-transparent")
    : "bg-white border-b border-black/5";

  const linkCls = dark ? "text-white/85 hover:text-[var(--dd-mustard)]" : "text-[var(--dd-green)] hover:text-[var(--dd-mustard-dark)]";
  const ctaCls = dark
    ? "bg-white text-[var(--dd-green)] hover:bg-white/90"
    : "bg-[var(--dd-green)] text-white hover:bg-[var(--dd-green-deep)]";

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${headerCls}`}
      data-testid="site-header"
    >
      <div className="px-5 md:px-10 lg:px-14">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2.5 shrink-0" data-testid="logo-link">
            {content?.logo_image ? (
              <img src={resolveImage(content.logo_image)} alt={content?.brand_name || "Dakar Street Food"} className="w-12 h-12 rounded-full object-cover ring-2 ring-[var(--dd-mustard)]/40" />
            ) : (
              <div className={`w-11 h-11 rounded-full flex items-center justify-center ${dark ? "bg-white" : "bg-[var(--dd-green)]"}`}>
                <span className={`font-display text-2xl ${dark ? "text-[var(--dd-green)]" : "text-[var(--dd-mustard)]"}`}>D</span>
              </div>
            )}
            <div className={`leading-none ${dark ? "text-white" : "text-[var(--dd-green)]"}`}>
              <div className="font-bold text-xl tracking-tight">{content?.brand_name || "Dakar Street Food"}</div>
              <div className={`text-[9px] uppercase tracking-[0.22em] mt-0.5 ${dark ? "text-white/55" : "text-[var(--dsf-text-muted)]"}`}>{content?.brand_tagline || "Houston · TX"}</div>
            </div>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-8">
            {NAV_LINKS.map((l) => (
              <Link key={l.to} to={l.to} className={`text-sm font-medium transition-colors ${linkCls}`} data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, '-')}`}>
                {l.label}
              </Link>
            ))}
          </nav>

          {/* Right actions */}
          <div className="flex items-center gap-2 md:gap-3 shrink-0">
            {/* Cart */}
            <Link
              to="/panier"
              className={`relative flex items-center gap-2 ${dark ? "text-white" : "text-[var(--dd-green)]"} hover:text-[var(--dd-mustard)] transition-colors`}
              data-testid="nav-cart"
            >
              <ShoppingCart size={20} />
              <span className="text-sm font-semibold hidden md:inline" data-testid="cart-count">Cart ({count})</span>
            </Link>

            {/* Login pill button OR account dropdown (unified — same icon for all roles, routes per role) */}
            {!isLoggedIn ? (
              <Link to="/connexion" className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-all ${ctaCls}`} data-testid="header-login-cta">
                Login
              </Link>
            ) : (
              <div className="relative">
                <button
                  onClick={() => setAccountOpen(!accountOpen)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-full ${dark ? "text-white" : "text-[var(--dd-green)]"}`}
                  data-testid="nav-account-button"
                  aria-label="Account"
                >
                  <div className="w-8 h-8 rounded-full bg-[var(--dd-mustard)] text-[var(--dd-green-deep)] flex items-center justify-center text-sm font-bold">
                    {(user?.name || user?.email || "?")[0].toUpperCase()}
                  </div>
                </button>
                {accountOpen && (
                  <div className="absolute right-0 top-full mt-2 w-56 bg-white border border-black/5 shadow-2xl rounded-2xl py-2 z-10" data-testid="account-dropdown">
                    <div className="px-4 py-3 border-b border-black/5">
                      <div className="font-semibold text-sm text-[var(--dd-green)]">{user?.name || profileLabel}</div>
                      <div className="text-xs text-[var(--dsf-text-muted)] truncate">{user?.email}</div>
                    </div>
                    <Link to={profileTarget} className="flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-[var(--dd-bone)] text-[var(--dsf-text)]" data-testid="nav-my-account">
                      <Package size={14} /> {profileLabel}
                    </Link>
                    <button onClick={() => { logout(); navigate("/"); }} className="flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-[var(--dd-bone)] w-full text-left text-[var(--dd-coral)]" data-testid="logout-button">
                      <LogOut size={14} /> Logout
                    </button>
                  </div>
                )}
              </div>
            )}

            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className={`lg:hidden p-2 rounded-full ${dark ? "text-white" : "text-[var(--dd-green)]"}`}
              data-testid="mobile-menu-toggle"
            >
              {mobileOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="lg:hidden bg-white border-t border-black/5 max-h-[80vh] overflow-y-auto" data-testid="mobile-menu">
          <nav className="flex flex-col px-6 py-6">
            {NAV_LINKS.map((l) => (
              <Link key={l.to} to={l.to} className="py-3 text-sm font-semibold text-[var(--dd-green)] border-b border-black/5">
                {l.label}
              </Link>
            ))}
            <div className="py-3 text-[10px] uppercase tracking-[0.32em] text-[var(--dd-green)] mt-4 font-semibold">{lang === "fr" ? "Boutique" : "Shop"}</div>
            <Link to="/univers/street_food" className="py-2 text-sm text-[var(--dsf-text-secondary)]">Dakar Street Food</Link>
            <Link to="/univers/street_market" className="py-2 text-sm text-[var(--dsf-text-secondary)]">Dakar Street Market</Link>
            <Link to="/univers/food_truck" className="py-2 text-sm text-[var(--dsf-text-secondary)]">Food Truck</Link>
            <Link to="/evenements" className="py-2 text-sm text-[var(--dsf-text-secondary)]">Dakar Events</Link>
            <div className="border-t border-black/5 mt-4 pt-4 flex items-center justify-between gap-4">
              <button onClick={() => setLang(lang === "fr" ? "en" : "fr")} className="flex items-center gap-2 text-xs uppercase tracking-wider text-[var(--dd-green)]">
                <Globe size={14} /> {lang === "fr" ? "English" : "Français"}
              </button>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
